import { PencilSimpleIcon, XIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import React from 'react'

import type { LinkAttachmentBaseProps } from '../../types'
import CardBody from '../_shared/CardBody'
import CardShell from '../_shared/CardShell'
import CardThumbnail, {
  AUDIO_BG_CLASS,
  isPlayableAudio,
} from '../_shared/CardThumbnail'
import { useChinPalette } from '../_shared/useChinPalette'

export interface ComposerCardProps extends LinkAttachmentBaseProps {
  /**
   * When provided, renders a dismiss X in the thumbnail corner. Called when
   * the composer clicks it to remove the attachment.
   */
  onDismiss?: () => void
  /**
   * When provided, renders a pencil button to the right of the description
   * that the composer can use to edit the attachment metadata.
   */
  onEditClick?: () => void
  /**
   * Accessible name for the dismiss button. Defaults to English because the
   * toolkit has no i18n layer; consumers that do (frontyard passes
   * `ComposerLinkPreviewList.closeLinkPreview`) should supply their own.
   */
  dismissLabel?: string
}

// 24x24 circle with a 12x12 white X — already Figma-exact.
const DISMISS_CLASS =
  'flex size-6 items-center justify-center rounded-full bg-[#121110] text-white focus-ring'

// Grows only the *clickable* area to 44x44 (24 + 10 + 10) for the platform
// touch minimum (MES-1350 / MES-1358), leaving the visual box alone — this
// same card renders in profiles' touch-first visitor composer, so a
// consumer-scoped fix could not reach it. Same idiom as the send button in
// `CustomMessageInput/index.tsx`. Not applied on the audio variant, where the
// button is an inline sibling of the native controls rather than an overlay
// and the halo would sit on top of them.
const DISMISS_TOUCH_TARGET_CLASS =
  "relative before:absolute before:-inset-[10px] before:content-['']"

/**
 * The card the composer sees while drafting a link attachment.
 * Matches the Composer column of the messaging design system in Figma.
 */
const ComposerCard: React.FC<ComposerCardProps> = ({
  title,
  placeholderTitle,
  description,
  descriptionLines,
  url,
  mimeType,
  thumbnailUrl,
  sourceUrl,
  layout = 'featured',
  appIcon,
  cta,
  accentColor,
  status = 'ready',
  onDismiss,
  onEditClick,
  dismissLabel = 'Dismiss attachment',
}) => {
  const isClassic = layout === 'classic'
  const isAudio = isPlayableAudio(mimeType, sourceUrl)
  const chin = useChinPalette({
    variant: 'dark',
    accentColor,
    status,
    layout,
    thumbnailUrl,
    mimeType,
    sourceUrl,
  })

  const dismissButton = onDismiss ? (
    <button
      type="button"
      onClick={onDismiss}
      aria-label={dismissLabel}
      className={classNames(
        DISMISS_CLASS,
        !isAudio && DISMISS_TOUCH_TARGET_CLASS
      )}
    >
      <XIcon className="size-3" weight="bold" />
    </button>
  ) : undefined

  const editButton = onEditClick ? (
    <button
      type="button"
      onClick={onEditClick}
      aria-label="Edit attachment"
      className="flex size-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15"
    >
      <PencilSimpleIcon className="size-5" weight="regular" />
    </button>
  ) : undefined

  // Audio cards collapse to just the native player — render the dismiss
  // button as an inline sibling so it always has its own space and never
  // overlaps the audio control's volume/menu buttons. Skipped while loading:
  // this path has no `CardBody` and no `busy`, so taking it during a scrape
  // would drop the skeleton and its `aria-busy` region entirely, and we do not
  // yet know the asset is audio anyway.
  if (isAudio && !chin.isLoading) {
    return (
      <CardShell variant="dark" bgClassName={AUDIO_BG_CLASS}>
        <div className="flex items-center gap-2 pr-3">
          <div className="min-w-0 flex-1">
            <CardThumbnail
              variant="dark"
              sourceUrl={sourceUrl}
              title={title}
              mimeType={mimeType}
            />
          </div>
          {dismissButton && <div className="shrink-0">{dismissButton}</div>}
        </div>
      </CardShell>
    )
  }

  return (
    <CardShell
      variant="dark"
      fixedHeight={!isClassic}
      accentHex={chin.accentHex}
      busy={chin.isLoading}
      topRight={isClassic ? dismissButton : undefined}
    >
      {!isClassic && (
        <CardThumbnail
          variant="dark"
          thumbnailUrl={chin.thumbnailUrl}
          sourceUrl={sourceUrl}
          title={title}
          mimeType={mimeType}
          topRight={dismissButton}
          isLoading={chin.isLoading}
          onImageError={chin.onImageError}
        />
      )}
      <CardBody
        chinTextColor={chin.chinTextColor}
        variant="dark"
        ctaSurface={chin.surface}
        title={title}
        placeholderTitle={placeholderTitle}
        description={description}
        descriptionLines={descriptionLines}
        url={url}
        appIcon={appIcon}
        cta={cta}
        trailingAction={editButton}
        isLoading={chin.isLoading}
      />
    </CardShell>
  )
}

export default ComposerCard
